import React, { memo, useCallback, useMemo } from "react";

import {
  ClickAwayListener,
  PopupPlacement,
  Unstable_Popup as Popup,
} from "@mui/base";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Grow, Paper, SxProps, Tooltip } from "@mui/material";

import { Emoticon } from "@/extensions/obituary/types";

import EmoticonButton from "./EmoticonButton";

const styles: Record<string, SxProps> = {
  root: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    maxWidth: 200,
    p: 1,
    background: "background.paper",
    position: "relative",
  },
};

export interface EmoticonPickerProps {
  anchor: React.RefObject<HTMLElement>;
  open: boolean;
  onClickAway: () => void;
  onEmoticonClick: (emoticon: Emoticon) => void;
  emoticons: Emoticon[];
  placement?: PopupPlacement;
  selectedIcon?: Emoticon | null;
  setSelectedIcon?: (emoticon: Emoticon | null) => void;
}

const SymbolPicker: React.FC<EmoticonPickerProps> = ({
  anchor,
  emoticons,
  selectedIcon,
  setSelectedIcon,
  open,
  onClickAway,
  onEmoticonClick,
  placement = "bottom-start",
}) => {
  const handleEmoticonClick = useCallback(
    (emoticon: Emoticon) => {
      setSelectedIcon?.(emoticon);
      onEmoticonClick(emoticon);
    },
    [onEmoticonClick, setSelectedIcon],
  );

  const handleClear = useCallback(() => {
    setSelectedIcon?.(null);
  }, [setSelectedIcon]);

  const renderedEmoticons = useMemo(() => {
    const items = emoticons.map((symbol) => (
      <EmoticonButton
        key={symbol.code}
        image={symbol.image}
        selected={symbol.code === selectedIcon?.code}
        onEmoticonClick={handleEmoticonClick}
        {...symbol}
      />
    ));

    if (setSelectedIcon && selectedIcon) {
      items.push(
        <Tooltip
          key="clear-icon"
          arrow
          disableInteractive
          slotProps={{
            tooltip: {
              sx: {
                zIndex: (theme) => theme.zIndex.tooltip + 1,
                pointerEvents: "none",
              },
            },
          }}
          title="Rensa ikon"
        >
          <Box
            aria-label="Rensa ikon"
            role="button"
            sx={{
              width: 28,
              height: 28,
              cursor: "pointer",
              backgroundColor: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: 0.5,
              border: "2px solid",
              borderColor: "error.main",
              color: "error.main",
            }}
            tabIndex={-1}
            onClick={handleClear}
          >
            <CloseIcon fontSize="small" />
          </Box>
        </Tooltip>,
      );
    }

    return items;
  }, [
    emoticons,
    handleClear,
    handleEmoticonClick,
    selectedIcon,
    setSelectedIcon,
  ]);

  return (
    <Popup
      anchor={anchor?.current}
      offset={4}
      open={open}
      placement={placement}
      slotProps={{
        root: {
          style: { zIndex: 1498 },
        },
      }}
    >
      <ClickAwayListener onClickAway={onClickAway}>
        <Grow in={open}>
          <Paper sx={styles.root} variant="outlined">
            {renderedEmoticons}
          </Paper>
        </Grow>
      </ClickAwayListener>
    </Popup>
  );
};

export default memo(SymbolPicker);
